// src/services/categoryService.js
import ApiService from './api'

class CategoryService {
  // Get all categories
  async getAllCategories(params = {}) {
    try {
      console.log('📦 Fetching all categories with params:', params)
      console.log('🧪 TEZLAA DEBUG: API URL for categories:', '/categories')
      const response = await ApiService.get('/categories', params)
      console.log('✅ Categories fetched successfully:', response)
      console.log('🧪 TEZLAA DEBUG: Categories response data:', JSON.stringify(response).substring(0, 200) + '...')
      
      // Check if we got valid data - categories should be in response.data.categories
      if (response && response.success && response.data && Array.isArray(response.data.categories)) {
        console.log('✅ Valid categories found:', response.data.categories.length)
        return response
      }
      
      // If no valid data, fall back to mock data
      console.log('⚠️ No valid category data from API, using fallback data')
      console.log('🧪 TEZLAA DEBUG: Expected array at response.data.categories, got:', typeof response?.data?.categories)
      return this.getFallbackCategories()
      
    } catch (error) {
      console.error('❌ Error fetching categories:', error)
      console.error('🧪 TEZLAA DEBUG: Category fetch error details:', error.message)
      console.log('⚠️ API failed, using fallback category data')
      return this.getFallbackCategories()
    }
  }

  // Fallback categories when API is not available
  getFallbackCategories() {
    const mockCategories = [
      {
        _id: '68ab4bbe75017ad6d7d8adf2',
        name: 'Hot Beverages',
        description: 'Coffee, Tea, Hot Chocolate',
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        _id: '68ab4b9675017ad6d7d8adbd',
        name: 'Desserts',
        description: 'Cakes, Pastries, Sweet Treats',
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        _id: '68ab4b8975017ad6d7d8adb8',
        name: 'Food',
        description: 'Sandwiches, Meals, Snacks',
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        _id: '68ab4b8975017ad6d7d8adb7',
        name: 'Cold Beverages',
        description: 'Iced Coffee, Soft Drinks, Juices',
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    ]

    return {
      success: true,
      data: mockCategories,
      message: 'Categories retrieved successfully (fallback data)'
    }
  }

  // Get category by ID
  async getCategoryById(id) {
    try {
      console.log('📦 Fetching category by ID:', id)
      const endpoint = ApiService.replaceUrlParams('/categories/:id', { id })
      const response = await ApiService.get(endpoint)
      console.log('✅ Category fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching category:', error)
      throw error
    }
  }

  // Create new category
  async createCategory(categoryData) {
    try {
      console.log('📦 Creating new category with data:', categoryData)
      const response = await ApiService.post('/categories', categoryData)
      console.log('✅ Category created successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error creating category:', error)
      throw error
    }
  }

  // Update category using PATCH
  async updateCategory(id, categoryData) {
    try {
      console.log('📦 Updating category:', { id, categoryData })
      const endpoint = ApiService.replaceUrlParams('/categories/:id', { id })
      console.log('🔗 Update endpoint:', endpoint)
      
      // Use PATCH instead of PUT for updates
      const response = await ApiService.patch(endpoint, categoryData)
      console.log('✅ Category updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error updating category:', error)
      throw error
    }
  }

  // Delete category
  async deleteCategory(id) {
    try {
      console.log('📦 Deleting category:', id)
      const endpoint = ApiService.replaceUrlParams('/categories/:id', { id })
      const response = await ApiService.delete(endpoint)
      console.log('✅ Category deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error deleting category:', error)
      throw error
    }
  }

  // Toggle category status using PATCH
  async toggleCategoryStatus(id, isActive) {
    try {
      console.log('📦 Toggling category status:', { id, isActive })
      const endpoint = ApiService.replaceUrlParams('/categories/:id', { id })
      const response = await ApiService.patch(endpoint, { isActive })
      console.log('✅ Category status toggled successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error toggling category status:', error)
      throw error
    }
  }

  // Search categories
  async searchCategories(searchTerm, filters = {}) {
    try {
      console.log('📦 Searching categories:', { searchTerm, filters })
      const params = {
        search: searchTerm,
        ...filters
      }
      const response = await ApiService.get('/categories', params)
      console.log('✅ Categories search completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error searching categories:', error)
      throw error
    }
  }

  // Get categories with pagination
  async getCategoriesPaginated(page = 1, limit = 10, filters = {}) {
    try {
      console.log('📦 Fetching paginated categories:', { page, limit, filters })
      const params = {
        page,
        limit,
        ...filters
      }
      const response = await ApiService.get('/categories', params)
      console.log('✅ Paginated categories fetched:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching paginated categories:', error)
      throw error
    }
  }

  // Bulk operations
  async bulkDeleteCategories(categoryIds) {
    try {
      console.log('📦 Bulk deleting categories:', categoryIds)
      const response = await ApiService.post('/categories/bulk-delete', {
        ids: categoryIds
      })
      console.log('✅ Categories bulk deleted successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error bulk deleting categories:', error)
      throw error
    }
  }

  // Bulk status update
  async bulkUpdateStatus(categoryIds, isActive) {
    try {
      console.log('📦 Bulk updating category status:', { categoryIds, isActive })
      const response = await ApiService.patch('/categories/bulk-status', {
        ids: categoryIds,
        isActive
      })
      console.log('✅ Categories bulk status updated successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error bulk updating category status:', error)
      throw error
    }
  }

  // Get category statistics
  async getCategoryStats() {
    try {
      console.log('📦 Fetching category statistics')
      const response = await ApiService.get('/categories/stats')
      console.log('✅ Category statistics fetched successfully:', response)
      return response
    } catch (error) {
      console.error('❌ Error fetching category statistics:', error)
      throw error
    }
  }

  // Validate category name (check for duplicates)
  async validateCategoryName(name, excludeId = null) {
    try {
      console.log('📦 Validating category name:', { name, excludeId })
      const params = {
        name,
        ...(excludeId && { excludeId })
      }
      const response = await ApiService.get('/categories/validate-name', params)
      console.log('✅ Category name validation completed:', response)
      return response
    } catch (error) {
      console.error('❌ Error validating category name:', error)
      throw error
    }
  }
}

export default new CategoryService()